import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Competitive Intelligence Simulation - TaskFlow Enterprise Solutions
 * 
 * Case Study Context:
 * - SaaS project management for 500-5,000 employee enterprises
 * - $142M ARR, 1,250 customers
 * - Before: $149.99/user, 28.5% market share, 29% win rate
 * - After: $154.99/user (+3.3%), 31.2% market share, 41% win rate
 * - Revenue increase: $8.7M annually
 * 
 * Visualization:
 * - Market share pie chart showing TaskFlow vs competitors
 * - Pricing elasticity curve showing demand response
 * - Win rate trends over time
 * - Competitor positioning matrix (price vs features)
 * - Real-time simulation of pricing changes affecting market share
 */

const CompetitiveSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    // TaskFlow SaaS - Competitive Intelligence State
    interface Competitor {
      name: string;
      price: number;
      marketShare: number;
      features: number; // 0-100 score
      color: p5.Color;
    }
    
    let competitors: Competitor[] = [];
    let taskflowPrice = 149.99; // Starting price
    let time = 0;
    let paused = false;
    let speed = 1;
    let selectedCompetitor = 0; // 0 = TaskFlow
    let showElasticity = false;
    
    // Historical data
    let winRateHistory: number[] = [];
    let marketShareHistory: number[] = [];
    
    p.setup = () => {
      p.createCanvas(1000, 650);
      p.frameRate(30);
      
      // Initialize competitors based on case study
      competitors = [
        {
          name: 'TaskFlow (You)',
          price: 149.99,
          marketShare: 28.5,
          features: 85,
          color: p.color(50, 150, 250)
        },
        {
          name: 'Competitor A',
          price: 159.99,
          marketShare: 32.0,
          features: 75,
          color: p.color(255, 100, 100)
        },
        {
          name: 'Competitor B',
          price: 139.99,
          marketShare: 22.5,
          features: 65,
          color: p.color(100, 200, 100)
        },
        {
          name: 'Competitor C',
          price: 179.99,
          marketShare: 12.0,
          features: 90,
          color: p.color(255, 200, 50)
        },
        {
          name: 'Others',
          price: 145.00,
          marketShare: 5.0,
          features: 60,
          color: p.color(150, 150, 150)
        }
      ];
      
      // Initialize history
      for (let i = 0; i < 60; i++) {
        winRateHistory.push(29); // Starting win rate
        marketShareHistory.push(28.5); // Starting market share
      }
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on pie chart to select competitor
        const pieX = 200;
        const pieY = 250;
        const dx = p.mouseX - pieX;
        const dy = p.mouseY - pieY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 100) {
          const angle = Math.atan2(dy, dx);
          let currentAngle = -p.HALF_PI;
          
          for (let i = 0; i < competitors.length; i++) {
            const sliceAngle = (competitors[i].marketShare / 100) * p.TWO_PI;
            if (angle >= currentAngle && angle < currentAngle + sliceAngle) {
              selectedCompetitor = i;
              break;
            }
            currentAngle += sliceAngle;
          }
        } else {
          paused = !paused;
        }
        return false;
      }
    };
    
    p.mouseDragged = () => {
      // Adjust TaskFlow pricing by dragging
      if (selectedCompetitor === 0 && p.mouseY > 100 && p.mouseY < 400) {
        taskflowPrice = p.map(p.mouseY, 400, 100, 130, 180);
        taskflowPrice = p.constrain(taskflowPrice, 130, 180);
        competitors[0].price = taskflowPrice;
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        // Reset to initial state
        taskflowPrice = 149.99;
        competitors[0].price = 149.99;
        competitors[0].marketShare = 28.5;
        winRateHistory = [];
        marketShareHistory = [];
        for (let i = 0; i < 60; i++) {
          winRateHistory.push(29);
          marketShareHistory.push(28.5);
        }
        time = 0;
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key === 'e' || p.key === 'E') showElasticity = !showElasticity;
      if (p.key >= '1' && p.key <= '5') {
        selectedCompetitor = parseInt(p.key) - 1;
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        time++;
        updateMarketDynamics();
      }
      
      // Draw visualization
      drawTitle();
      drawMarketSharePie();
      drawPricingMatrix();
      drawTrends();
      drawMetrics();
      drawControls();
    };
    
    const updateMarketDynamics = () => {
      // Pricing elasticity model: demand changes with price
      // Elasticity = -1.8 (from case study)
      const elasticity = -1.8;
      const basePrice = 149.99;
      const priceChange = (competitors[0].price - basePrice) / basePrice;
      const demandChange = elasticity * priceChange;
      
      // Calculate new market share based on price and features
      let newMarketShare = 28.5 * (1 + demandChange);
      
      // Feature advantage boosts share
      const avgFeatures = competitors.reduce((sum, c) => sum + c.features, 0) / competitors.length;
      const featureAdvantage = (competitors[0].features - avgFeatures) / avgFeatures;
      newMarketShare *= (1 + featureAdvantage * 0.3);
      
      // Constrain to realistic range
      newMarketShare = p.constrain(newMarketShare, 15, 45);
      
      // Smooth transition
      competitors[0].marketShare = p.lerp(competitors[0].marketShare, newMarketShare, 0.05);
      
      // Redistribute remaining share among competitors
      const remainingShare = 100 - competitors[0].marketShare;
      const otherTotal = competitors.slice(1).reduce((sum, c) => sum + c.marketShare, 0);
      for (let i = 1; i < competitors.length; i++) {
        competitors[i].marketShare = (competitors[i].marketShare / otherTotal) * remainingShare;
      }
      
      // Calculate win rate based on pricing position
      const avgPrice = competitors.reduce((sum, c) => sum + c.price * c.marketShare / 100, 0);
      const pricePosition = competitors[0].price / avgPrice;
      let winRate = 29; // Base
      
      if (pricePosition < 0.95) {
        winRate = 35 + (0.95 - pricePosition) * 100; // Lower price = higher win rate
      } else if (pricePosition > 1.05) {
        winRate = 29 - (pricePosition - 1.05) * 50; // Higher price = lower win rate
      } else {
        winRate = 38; // Sweet spot around market average
      }
      
      winRate = p.constrain(winRate, 15, 50);
      
      // Update history
      winRateHistory.push(winRate);
      if (winRateHistory.length > 60) winRateHistory.shift();
      
      marketShareHistory.push(competitors[0].marketShare);
      if (marketShareHistory.length > 60) marketShareHistory.shift();
    };
    
    const drawTitle = () => {
      p.fill(30);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(16);
      p.text('Competitive Intelligence Simulation', 20, 20);
      
      p.textSize(12);
      p.fill(80);
      p.text('TaskFlow vs Market Leaders - Real-time Market Dynamics', 20, 45);
    };
    
    const drawMarketSharePie = () => {
      const pieX = 200;
      const pieY = 270;
      const pieR = 85;
      
      // Background panel
      p.fill(248);
      p.stroke(220);
      p.strokeWeight(1);
      p.rect(30, 100, 340, 360, 8);
      
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(14);
      p.fill(30);
      p.noStroke();
      p.text('Market Share Distribution', 200, 115);
      
      let currentAngle = -p.HALF_PI;
      
      // Draw pie slices
      for (let i = 0; i < competitors.length; i++) {
        const sliceAngle = (competitors[i].marketShare / 100) * p.TWO_PI;
        
        const isSelected = i === selectedCompetitor;
        const r = isSelected ? pieR + 5 : pieR;
        
        p.fill(competitors[i].color);
        p.stroke(255);
        p.strokeWeight(2);
        p.arc(pieX, pieY, r * 2, r * 2, currentAngle, currentAngle + sliceAngle, p.PIE);
        
        currentAngle += sliceAngle;
      }
      
      // Draw labels inside panel with lines
      currentAngle = -p.HALF_PI;
      for (let i = 0; i < competitors.length; i++) {
        const sliceAngle = (competitors[i].marketShare / 100) * p.TWO_PI;
        const midAngle = currentAngle + sliceAngle / 2;
        
        // Calculate label position, constrained within panel
        const innerX = pieX + Math.cos(midAngle) * (pieR + 8);
        const innerY = pieY + Math.sin(midAngle) * (pieR + 8);
        
        // Constrain label position to stay within bounds
        let labelDist = pieR + 40;
        let outerX = pieX + Math.cos(midAngle) * labelDist;
        let outerY = pieY + Math.sin(midAngle) * labelDist;
        
        // Keep within panel bounds (30-370 x, 100-460 y)
        outerX = p.constrain(outerX, 45, 355);
        outerY = p.constrain(outerY, 145, 445);
        
        // Draw connecting line
        p.stroke(150);
        p.strokeWeight(1);
        p.line(innerX, innerY, outerX, outerY);
        
        // Label text
        p.fill(30);
        p.noStroke();
        p.textSize(9);
        
        // Align text based on side
        if (Math.cos(midAngle) > 0) {
          p.textAlign(p.LEFT, p.CENTER);
          p.text(`${competitors[i].name.split(' ')[0]}: ${competitors[i].marketShare.toFixed(1)}%`, outerX + 3, outerY);
        } else {
          p.textAlign(p.RIGHT, p.CENTER);
          p.text(`${competitors[i].name.split(' ')[0]}: ${competitors[i].marketShare.toFixed(1)}%`, outerX - 3, outerY);
        }
        
        currentAngle += sliceAngle;
      }
    };
    
    const drawPricingMatrix = () => {
      const matrixX = 420;
      const matrixY = 140;
      const matrixW = 540;
      const matrixH = 280;
      
      // Background panel
      p.fill(248);
      p.stroke(220);
      p.strokeWeight(1);
      p.rect(matrixX - 10, matrixY - 40, matrixW + 20, matrixH + 50, 8);
      
      // Matrix background
      p.fill(255);
      p.stroke(200);
      p.rect(matrixX, matrixY, matrixW, matrixH);
      
      // Title
      p.fill(30);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('Competitive Positioning Matrix', matrixX, matrixY - 25);
      
      // Axes
      p.stroke(100);
      p.line(matrixX, matrixY + matrixH, matrixX + matrixW, matrixY + matrixH); // X-axis
      p.line(matrixX, matrixY, matrixX, matrixY + matrixH); // Y-axis
      
      // Labels
      p.fill(100);
      p.noStroke();
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(10);
      p.text('Price per User/Month ($)', matrixX + matrixW / 2, matrixY + matrixH + 5);
      
      p.push();
      p.translate(matrixX - 25, matrixY + matrixH / 2);
      p.rotate(-p.HALF_PI);
      p.text('Feature Score', 0, 0);
      p.pop();
      
      // Plot competitors
      for (let i = 0; i < competitors.length; i++) {
        const x = p.map(competitors[i].price, 130, 180, matrixX + 20, matrixX + matrixW - 20);
        const y = p.map(competitors[i].features, 50, 100, matrixY + matrixH - 20, matrixY + 20);
        
        // Circle size based on market share
        const size = 10 + competitors[i].marketShare * 0.5;
        
        p.fill(competitors[i].color);
        p.stroke(i === selectedCompetitor ? 255 : 200);
        p.strokeWeight(i === selectedCompetitor ? 3 : 1);
        p.circle(x, y, size);
        
        // Label
        p.fill(40);
        p.noStroke();
        p.textAlign(p.CENTER, p.BOTTOM);
        p.textSize(9);
        p.text(competitors[i].name.split(' ')[0], x, y - size / 2 - 3);
      }
      
      // Optimal zone (high features, competitive price)
      p.fill(100, 255, 100, 30);
      p.noStroke();
      const optX1 = p.map(145, 130, 180, matrixX + 20, matrixX + matrixW - 20);
      const optX2 = p.map(160, 130, 180, matrixX + 20, matrixX + matrixW - 20);
      const optY1 = p.map(80, 50, 100, matrixY + matrixH - 20, matrixY + 20);
      const optY2 = p.map(95, 50, 100, matrixY + matrixH - 20, matrixY + 20);
      p.rect(optX1, optY2, optX2 - optX1, optY1 - optY2);
      
      p.fill(100, 180, 100);
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(9);
      p.text('Sweet Spot', (optX1 + optX2) / 2, (optY1 + optY2) / 2);
    };
    
    const drawTrends = () => {
      const trendX = 40;
      const trendY = 490;
      const trendW = 920;
      const trendH = 110;
      
      // Background panel
      p.fill(248);
      p.stroke(220);
      p.strokeWeight(1);
      p.rect(trendX - 10, trendY - 30, trendW + 20, trendH + 50, 8);
      
      // Chart background
      p.fill(255);
      p.stroke(200);
      p.rect(trendX, trendY, trendW, trendH);
      
      // Title
      p.fill(30);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('Performance Trends (Last 60 periods)', trendX, trendY - 25);
      
      // Win rate line
      p.stroke(50, 150, 250);
      p.strokeWeight(2);
      p.noFill();
      p.beginShape();
      for (let i = 0; i < winRateHistory.length; i++) {
        const x = p.map(i, 0, 60, trendX + 10, trendX + trendW - 10);
        const y = p.map(winRateHistory[i], 15, 50, trendY + trendH - 10, trendY + 10);
        p.vertex(x, y);
      }
      p.endShape();
      
      // Market share line
      p.stroke(100, 200, 100);
      p.beginShape();
      for (let i = 0; i < marketShareHistory.length; i++) {
        const x = p.map(i, 0, 60, trendX + 10, trendX + trendW - 10);
        const y = p.map(marketShareHistory[i], 15, 45, trendY + trendH - 10, trendY + 10);
        p.vertex(x, y);
      }
      p.endShape();
      
      // Legend
      p.fill(50, 150, 250);
      p.noStroke();
      p.rect(trendX + 10, trendY + 5, 12, 8);
      p.fill(40);
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(9);
      p.text('Win Rate (%)', trendX + 25, trendY + 3);
      
      p.fill(100, 200, 100);
      p.rect(trendX + 110, trendY + 5, 12, 8);
      p.fill(40);
      p.text('Market Share (%)', trendX + 125, trendY + 3);
    };
    
    const drawMetrics = () => {
      const metricsX = 420;
      const metricsY = 55;
      
      // Background panel
      p.fill(248);
      p.stroke(220);
      p.strokeWeight(1);
      p.rect(metricsX - 10, metricsY - 10, 550, 40, 8);
      
      p.fill(30);
      p.noStroke();
      p.textAlign(p.LEFT, p.CENTER);
      p.textSize(11);
      
      const currentWinRate = winRateHistory[winRateHistory.length - 1];
      const currentShare = marketShareHistory[marketShareHistory.length - 1];
      const revenue = (currentShare / 100) * 2400; // $2.4B total market
      
      p.text(`Price: $${competitors[0].price.toFixed(2)}/user | Share: ${currentShare.toFixed(1)}% | Revenue: $${revenue.toFixed(0)}M | Win Rate: ${currentWinRate.toFixed(1)}%`, metricsX, metricsY + 10);
      
      // Recommendation
      const optimalPrice = 154.99;
      const priceDiff = competitors[0].price - optimalPrice;
      
      p.textSize(10);
      if (Math.abs(priceDiff) < 2) {
        p.fill(50, 180, 50);
        p.text('✓ Optimal', metricsX + 430, metricsY + 10);
      } else if (priceDiff < -5) {
        p.fill(200, 150, 50);
        p.text('⚠ Too Low', metricsX + 430, metricsY + 10);
      } else if (priceDiff > 5) {
        p.fill(200, 50, 50);
        p.text('⚠ Too High', metricsX + 430, metricsY + 10);
      }
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.BOTTOM);
      p.textSize(10);
      
      p.text('Click: Select | Drag: Adjust Price | Space: Pause | R: Reset | +/-: Speed | 1-5: Select Competitor', 50, 640);
      
      p.textAlign(p.RIGHT, p.BOTTOM);
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : `RUNNING (${speed.toFixed(1)}x)`, 950, 640);
    };
  };

  const aboutContent = (
    <div className="space-y-6 text-sm">
      <div>
        <h4 className="font-semibold text-gray-900 mb-2">How This Simulation Works</h4>
        <p className="text-gray-700 mb-3">
          This Competitive Intelligence simulation models TaskFlow, a SaaS project management platform, competing in the enterprise market. The simulation demonstrates how pricing decisions impact market share, win rates, and revenue through real-time market dynamics.
        </p>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Core Mechanics</h5>
        <ul className="list-disc list-inside space-y-1 text-gray-700">
          <li><strong>Pricing Elasticity:</strong> Market demand responds to price changes with elasticity of -1.8 (based on SaaS market research)</li>
          <li><strong>Feature Advantage:</strong> Superior features provide competitive edge and market share boost</li>
          <li><strong>Win Rate Dynamics:</strong> Pricing relative to market average affects sales win probability</li>
          <li><strong>Market Redistribution:</strong> When TaskFlow gains/loses share, competitors adjust accordingly</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Visualizations Explained</h5>
        <div className="space-y-2 text-gray-700">
          <p><strong>Market Share Pie Chart:</strong> Shows current market distribution. Click slices to select competitors. Drag TaskFlow's slice up/down to adjust pricing.</p>
          <p><strong>Positioning Matrix:</strong> Plots competitors by price (X-axis) vs features (Y-axis). Bubble size indicates market share. The green "Sweet Spot" zone represents optimal pricing-feature balance.</p>
          <p><strong>Performance Trends:</strong> Tracks win rate (blue) and market share (green) over 60 periods, showing historical impact of pricing decisions.</p>
          <p><strong>Current Performance Panel:</strong> Displays real-time metrics and optimization recommendations based on pricing analysis.</p>
        </div>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Mathematical Model</h5>
        <div className="bg-gray-50 p-3 rounded text-xs font-mono space-y-1">
          <p>Demand Change = Elasticity × Price Change</p>
          <p>Market Share = Base Share × (1 + Demand Change) × (1 + Feature Advantage × 0.3)</p>
          <p>Win Rate = Base Rate + Price Position Adjustment</p>
          <p>Revenue = Market Share × Total Market Size</p>
        </div>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Interactive Controls</h5>
        <ul className="list-disc list-inside space-y-1 text-gray-700">
          <li><strong>Click Pie Slices:</strong> Select different competitors to analyze</li>
          <li><strong>Drag Vertically:</strong> Adjust TaskFlow pricing in real-time</li>
          <li><strong>Spacebar:</strong> Pause/resume simulation</li>
          <li><strong>R Key:</strong> Reset to initial conditions</li>
          <li><strong>+/- Keys:</strong> Control simulation speed</li>
          <li><strong>Number Keys 1-5:</strong> Quick competitor selection</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Key Insights</h5>
        <p className="text-gray-700">
          The simulation reveals that optimal pricing isn't about being the cheapest - it's about finding the sweet spot where price, features, and market position align. TaskFlow's optimal price ($154.99) maximizes revenue by balancing market share retention with per-unit pricing, demonstrating classic competitive optimization in action.
        </p>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Business Context</h5>
        <p className="text-gray-700">
          Based on real SaaS market data, this simulation represents a $2.4B total addressable market with enterprise customers (500-5,000 employees). The competitive dynamics reflect actual challenges faced by B2B SaaS companies in balancing growth, profitability, and market positioning.
        </p>
      </div>
    </div>
  );

  return (
    <BaseSimulation
      sketch={sketch}
      title="Competitive Intelligence Simulation"
      description="Interactive visualization of TaskFlow's competitive positioning in the SaaS project management market. Shows market share dynamics, pricing elasticity, and competitive positioning based on real market data."
      aboutContent={aboutContent}
    />
  );
};

export default CompetitiveSimulation;
